import React, { useEffect } from 'react';
import { Bell, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { NotificationsApi, NotificationSettings } from 'api-client';
import { useTeams } from '@/contexts/TeamsContext';
import { AuthContext } from '@/contexts/AuthContext';
import { useContext, useState } from 'react';
import { getApiConfiguration } from '@/api/utils';
import { toast } from 'sonner';

export const AlertsSettingsPage = () => {
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const client = new NotificationsApi(getApiConfiguration(token));
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [errors, setErrors] = useState<{
    notificationEmail?: string;
    slackWebhookUrl?: string;
  }>({});

  const getNotificationSettings = async () => {
    try {
      if (!token || !currentTeam?.slug) return;
      const response = await client.notificationsSettingsRetrieve({ id: currentTeam.slug, teamSlug: currentTeam.slug });
      setSettings(response);
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      toast.error('Failed to fetch notification settings');
    }
  };

  useEffect(() => {
    getNotificationSettings();
  }, [token, currentTeam?.slug]);

  const validateEmail = (email: string | null | undefined): boolean => {
    if (!email) return true; // Empty email is valid (optional field)
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validateSlackWebhook = (url: string | null | undefined): boolean => {
    if (!url) return true; // Empty URL is valid (optional field)
    try {
      const webhookUrl = new URL(url);
      return webhookUrl.protocol === 'https:' && webhookUrl.hostname.includes('hooks.slack.com');
    } catch {
      return false;
    }
  };

  const validateSettings = (): boolean => {
    const newErrors: { notificationEmail?: string; slackWebhookUrl?: string } = {};
    
    if (settings?.notificationEmail && !validateEmail(settings.notificationEmail)) {
      newErrors.notificationEmail = 'Please enter a valid email address';
    }
    
    if (settings?.slackWebhookUrl && !validateSlackWebhook(settings.slackWebhookUrl)) {
      newErrors.slackWebhookUrl = 'Please enter a valid Slack webhook URL (https://hooks.slack.com/...)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!token || !currentTeam?.slug) return;
    
    if (!validateSettings()) {
      toast.error('Please fix the validation errors before saving');
      return;
    }
    
    try {
      const response = await client.notificationsSettingsUpdate({
        id: currentTeam.slug,
        teamSlug: currentTeam.slug,
        notificationSettings: settings || undefined
      });
      setSettings(response);
      toast.success('Notification settings updated successfully');
    } catch (error) {
      console.error('Error updating notification settings:', error);
      toast.error('Failed to update notification settings');
    }
  };

  // Helper function to handle setting changes in a type-safe way
  const handleSettingChange = (key: keyof Omit<NotificationSettings, 'teamSlug'>, value: boolean | string) => {
    if (!settings) return;
    
    // Create a proper copy with all required fields
    const updatedSettings = {
      ...settings,
      [key]: value
    };
    
    setSettings(updatedSettings);
  };

  const onReset = () => {
    if (!settings) return;
    
    // Create a new settings object with all notification properties set to true
    const updatedSettings = { ...settings };
    
    // Type-safe way to update specific notification properties
    if ('slackWebhookUrl' in updatedSettings) updatedSettings.slackWebhookUrl = '';
    if("notificationEmail" in updatedSettings) updatedSettings.notificationEmail = '';
    
    setSettings(updatedSettings);
    handleSubmit();
  };

  return (
    <div className="space-y-6">
      <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Bell className="h-5 w-5 text-blue-500" />
          <h2 className="text-lg font-semibold text-white">Alerts Settings</h2>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="slackWebhookUrl" className="text-sm font-medium text-white">Slack Webhook URL</label>
            <input
              id="slackWebhookUrl"
              name="slackWebhookUrl"
              type="text"
              value={settings?.slackWebhookUrl || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleSettingChange('slackWebhookUrl', e.target.value);
                // Clear error when user starts typing
                if (errors.slackWebhookUrl) {
                  setErrors({ ...errors, slackWebhookUrl: undefined });
                }
              }}
              className={`w-full px-3 py-2 bg-gray-800/50 border ${errors.slackWebhookUrl ? 'border-red-500' : 'border-gray-700'} rounded-md text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50`}
            />
            {errors.slackWebhookUrl && (
              <div className="flex items-center mt-1 text-xs text-red-500">
                <AlertCircle className="h-3 w-3 mr-1" />
                {errors.slackWebhookUrl}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <label htmlFor="notificationEmail" className="text-sm font-medium text-white">Notification Email</label>
            <input
              id="notificationEmail"
              name="notificationEmail"
              type="email"
              value={settings?.notificationEmail || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleSettingChange('notificationEmail', e.target.value);
                // Clear error when user starts typing
                if (errors.notificationEmail) {
                  setErrors({ ...errors, notificationEmail: undefined });
                }
              }}
              className={`w-full px-3 py-2 bg-gray-800/50 border ${errors.notificationEmail ? 'border-red-500' : 'border-gray-700'} rounded-md text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50`}
            />
            {errors.notificationEmail && (
              <div className="flex items-center mt-1 text-xs text-red-500">
                <AlertCircle className="h-3 w-3 mr-1" />
                {errors.notificationEmail}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t border-gray-800">
          <Button variant="outline" onClick={onReset}>Reset to Default</Button>
          <Button onClick={handleSubmit}>Save Changes</Button>
        </div>
      </div>
    </div>
  );
};